import { ReactNode, useCallback, useEffect, useState } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { formatNumber } from "@malond/utils"
import { cn } from "@malond/ui/utils"

interface PaginationButtonProps {
  pageIndex: number
  buttonIndex: number
  onClick: (pageIndex: number) => void
  className?: string
  children?: ReactNode
  disabled?: boolean
}

function PaginationButton({
  pageIndex,
  buttonIndex,
  onClick,
  className = "",
  children,
  disabled = false,
}: PaginationButtonProps) {
  const isActive = pageIndex === buttonIndex

  return (
    <button
      type="button"
      aria-current={isActive ? "page" : undefined}
      disabled={disabled}
      className={cn(
        "relative inline-flex items-center border px-4 py-2 text-sm font-medium transition-colors focus:z-20",
        !isActive &&
          "border-border bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground",
        isActive &&
          "z-10 border-primary bg-primary font-semibold text-primary-foreground",
        disabled &&
          "cursor-not-allowed bg-muted opacity-40 hover:bg-muted hover:text-muted-foreground",
        className
      )}
      onClick={() => (disabled ? false : onClick(buttonIndex))}
    >
      {children || ""}
    </button>
  )
}

interface PaginationTotalTitle {
  page: number
  pageSize: number
  total: number
  minimum?: boolean
  className?: string
}

function TotalTitle({
  page,
  pageSize,
  total,
  minimum,
  className,
}: PaginationTotalTitle) {
  const from = total > 0 ? page * pageSize + 1 : 0
  const to = Math.min(total, (page + 1) * pageSize)

  return (
    <div>
      <p className={cn("text-sm text-muted-foreground", className)}>
        {!minimum ? "Показано с " : ""}
        <span className="font-medium text-foreground">
          {formatNumber(from)}
        </span>{" "}
        по{" "}
        <span className="font-medium text-foreground">{formatNumber(to)}</span>{" "}
        из{" "}
        <span className="font-medium text-foreground">
          {formatNumber(total)}
        </span>
        {!minimum ? " элементов" : ""}
      </p>
    </div>
  )
}

const getPageIndexFromPage = (
  page: number | undefined,
  pageSize: number,
  total: number
): number => {
  return page && page > 0 && page <= Math.ceil(total / pageSize) ? page - 1 : 0
}

export interface PaginationProps {
  page?: number
  pageSize: number
  total: number
  onChange?: (page: number, pageSize: number) => void | Promise<void>
}

export function Pagination({
  page,
  pageSize,
  total,
  onChange,
}: PaginationProps) {
  const [pageIndex, setPageIndex] = useState(
    getPageIndexFromPage(page, pageSize, total)
  )

  useEffect(() => {
    setPageIndex(getPageIndexFromPage(page, pageSize, total))
  }, [page, pageSize, total])

  const countPages = Math.ceil(total / pageSize)

  const disabledLeft = total === 0 || pageIndex === 0
  const disabledRight = total === 0 || pageIndex === countPages - 1

  const onChangePage = (buttonIndex: number) => {
    setPageIndex(buttonIndex)

    if (onChange) {
      onChange(buttonIndex + 1, pageSize)
    }
  }

  const renderPageLinks = useCallback(() => {
    if (countPages === 0) {
      return null
    }

    const visiblePageButtonCount = 5
    let numberOfButtons =
      countPages < visiblePageButtonCount ? countPages : visiblePageButtonCount

    const pageIndices = [pageIndex]
    numberOfButtons--

    ;[...Array(numberOfButtons)].forEach((_item, itemIndex) => {
      const pageNumberBefore = pageIndices[0] - 1

      const pageNumberAfter = pageIndices[pageIndices.length - 1] + 1
      if (
        pageNumberBefore >= 0 &&
        (itemIndex < numberOfButtons / 2 || pageNumberAfter > countPages - 1)
      ) {
        pageIndices.unshift(pageNumberBefore)
      } else {
        pageIndices.push(pageNumberAfter)
      }
    })

    return pageIndices.map((pageIndexToMap) => (
      <PaginationButton
        key={pageIndexToMap}
        pageIndex={pageIndex}
        buttonIndex={pageIndexToMap}
        onClick={onChangePage}
      >
        {pageIndexToMap + 1}
      </PaginationButton>
    ))
  }, [countPages, pageIndex])

  return (
    <div className="flex flex-col items-center justify-between border-t border-border bg-card px-4 py-3 sm:flex-row sm:px-6">
      {/* Mobile pagination */}
      <div className="flex w-full flex-1 flex-col sm:hidden">
        <TotalTitle
          page={pageIndex}
          pageSize={pageSize}
          total={total}
          minimum={true}
          className="pb-4 text-center"
        />

        <div className="flex flex-1 justify-between gap-2">
          <PaginationButton
            pageIndex={pageIndex}
            buttonIndex={pageIndex - 1}
            onClick={onChangePage}
            className="flex-1 justify-center rounded-md"
            disabled={disabledLeft}
          >
            Предыдущие
          </PaginationButton>
          <PaginationButton
            pageIndex={pageIndex}
            buttonIndex={pageIndex + 1}
            onClick={onChangePage}
            className="flex-1 justify-center rounded-md"
            disabled={disabledRight}
          >
            Следующие
          </PaginationButton>
        </div>
      </div>

      {/* Desktop pagination */}
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <TotalTitle page={pageIndex} pageSize={pageSize} total={total} />

        <div>
          <nav
            className="isolate inline-flex -space-x-px rounded-md shadow-xs"
            aria-label="Pagination"
          >
            <PaginationButton
              pageIndex={pageIndex}
              buttonIndex={pageIndex - 1}
              onClick={onChangePage}
              className="rounded-l-md px-2 py-2"
              disabled={disabledLeft}
            >
              <span className="sr-only">Предыдущие</span>
              <ChevronLeft className="size-5" aria-hidden="true" />
            </PaginationButton>

            {/* Main numbers */}
            {renderPageLinks()}

            <PaginationButton
              pageIndex={pageIndex}
              buttonIndex={pageIndex + 1}
              onClick={onChangePage}
              className="rounded-r-md px-2 py-2"
              disabled={disabledRight}
            >
              <span className="sr-only">Следующие</span>
              <ChevronRight className="size-5" aria-hidden="true" />
            </PaginationButton>
          </nav>
        </div>
      </div>
    </div>
  )
}
